import { useCallback, useEffect, useRef, useState } from "react";
import type { SocialPostDetailDto } from "@multi-chat/shared";
import { getSocialPost } from "../../../api/social-comments-api.js";

export type SocialPostView =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; post: SocialPostDetailDto };

export function useSocialPost(
  organizationId: number,
  postId: string | null,
  unreachableMessage: string,
  enabled = true,
): {
  postView: SocialPostView;
  refresh: () => void;
} {
  const [postView, setPostView] = useState<SocialPostView>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  const orgRef = useRef(organizationId);
  const postRef = useRef(postId);
  const enabledRef = useRef(enabled);

  const load = useCallback(async () => {
    const orgId = organizationId;
    const id = postId;
    if (!enabledRef.current || id == null) {
      setPostView({ kind: "idle" });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPostView((current) => (current.kind === "ready" ? current : { kind: "loading" }));
    try {
      const post = await getSocialPost(orgId, id, controller.signal);
      if (controller.signal.aborted || orgRef.current !== orgId || postRef.current !== id) {
        return;
      }
      setPostView({ kind: "ready", post });
    } catch {
      if (controller.signal.aborted || orgRef.current !== orgId || postRef.current !== id) {
        return;
      }
      setPostView({ kind: "error", message: unreachableMessage });
    }
  }, [organizationId, postId, unreachableMessage]);

  useEffect(() => {
    enabledRef.current = enabled;
    orgRef.current = organizationId;
    postRef.current = postId;
    if (!enabled || postId == null) {
      setPostView({ kind: "idle" });
      abortRef.current?.abort();
      return;
    }
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, load, organizationId, postId]);

  return {
    postView,
    refresh: () => {
      void load();
    },
  };
}
